'use client'

import { usePathname } from 'next/navigation'
import Image from 'next/image'
import Container from '@/components/structure/Container'
import Title from '@/components/content/common/Title'
import SectionFooter from '@/components/content/home/shared/SectionFooter'
import { LogoText } from '@/components/ui/Brand'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import { ReactNode } from 'react'

type Versus = {
  title: string
  type: 'negative' | 'positive'
  items: ReactNode[]
}

export const versus: Versus[] = [
  {
    title: 'Other review sites',
    type: 'negative',
    items: [
      <p>
        Pay to feature the <b>highest bidder</b>
      </p>,
      <p>
        Focus on <b>clunky</b> enterprise tools
      </p>,
      <p>
        <b>Sell your data</b> as leads
      </p>,
      <p>
        Pay users for <b>filler</b> reviews
      </p>,
      <p>
        Show <b className="hidden md:inline">filler</b>{' '}
        <span className="hidden md:inline">and</span> <b>complex</b> comparison charts
      </p>,
      <p>
        Offer <b>no real</b> recommendations
      </p>,
      <p>
        Promote <b>legacy</b> and <b>outdated</b> tools
      </p>,
    ],
  },
  {
    title: 'Efficient App',
    type: 'positive',
    items: [
      <p>
        Curate only the <b>best</b> tools
      </p>,
      <p>
        Focus on <b>modern</b> and <b>fast</b> software
      </p>,
      <p>
        <b>Never sell</b> your data
      </p>,
      <p>
        Write every review <b>ourselves</b>
      </p>,
      <p>
        Keep lists <b>short</b> and <b>scannable</b>
      </p>,
      <p>
        Give <b>clear</b> and <b>honest</b> recs
      </p>,
      <p>
        Highlight <b>game-changing</b> new apps
      </p>,
    ],
  },
]

export default function HomeVersus() {
  const pathname = usePathname()
  const link = pathname === '/about' ? '/methodology' : '/about'

  return (
    <Container reduced={true}>
      <Title
        title="We test <span>everything</span> so you don’t have to"
        subtitle="The efficient way"
        icon={<Icon name="user-sparkle" />}
        paragraphs={[
          'Every tool is tested hands-on.',
          'No sponsorships. No fluff. Just honest breakdowns. ',
        ]}
        buttons={[
          { label: 'Explore apps reviews', link: '/apps' },
          { label: 'Read our methodology', link: '/methodology' },
        ]}
        type="primary"
        className="mb-10"
      />

      <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-6">
        {versus.map((list, index) => (
          <VersusList key={index} {...list} />
        ))}
      </div>

      <SectionFooter
        icon={<Icon name="ruler" size={12} />}
        text="Independent, all-human, hands-on testing."
        link={link}
        linkText="Learn how we review"
      />
    </Container>
  )
}

function VersusList({ title, type, items }: Versus) {
  return (
    <div>
      <VersusHeader title={title} type={type} />
      <ul className="flex flex-col">
        {items.map((item, index) => (
          <VersusItem key={index} item={item} type={type} />
        ))}
      </ul>
    </div>
  )
}

function VersusHeader({ title, type }: { title: string; type: Versus['type'] }) {
  return (
    <div
      className={cn(
        'relative flex h-16 items-center overflow-hidden rounded-t-2xl px-4.5 shadow-inset',
        type === 'positive' ? 'bg-primary-300/5' : 'bg-gray-100',
      )}
    >
      {type === 'positive' && (
        <>
          <div className="absolute left-0 top-0 z-10 h-full w-32 bg-gradient-to-r from-fuchsia-600 via-violet-700 via-30% to-transparent opacity-20"></div>
          <div className="absolute right-0 top-0 z-10 h-full w-48 bg-gradient-to-l from-fuchsia-600 via-violet-700/50 via-70% to-transparent opacity-40"></div>
        </>
      )}

      <div className="relative z-20 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-black/10 bg-white shadow-button">
        {type === 'positive' ? (
          <Image
            src="/icons/web-app-manifest-192x192.png"
            alt={title}
            width={32}
            height={32}
            className="pr-0.5 pt-0.5"
          />
        ) : (
          <span className="h-5 w-5 rounded-full border border-gray-300 bg-gray-100"></span>
        )}
      </div>

      <div className="flex w-full items-center justify-center pr-4 md:pr-3">
        {type === 'positive' ? (
          <LogoText />
        ) : (
          <span className="text-center text-14 font-semibold">{title}</span>
        )}
      </div>

      {type === 'positive' && (
        <div className="absolute -right-4 bottom-0 z-20 h-14 w-48 md:h-16">
          <Image
            src="/img/home/alex-andra.png"
            alt="Alex and Andra"
            fill
            sizes="(max-width: 768px) 100vw, 600px"
            className="object-cover object-right-bottom"
          />
        </div>
      )}
    </div>
  )
}

function VersusItem({ item, type }: { item: ReactNode; type: Versus['type'] }) {
  return (
    <li className="flex h-16 items-center border-x border-b border-black/10 px-6 last:rounded-b-2xl">
      {type === 'positive' ? (
        <div className="text-success-500">
          <Icon name="circle-check" />
        </div>
      ) : (
        <div className="text-primary-500">
          <Icon name="circle-xmark" />
        </div>
      )}
      <div
        className={cn(
          'w-full truncate px-4 text-center text-14 text-gray-700 [&>p>b]:rounded-md [&>p>b]:px-1.5 [&>p>b]:py-1 [&>p>b]:font-medium',
          type === 'positive'
            ? '[&>p>b]:bg-success-500/10 [&>p>b]:text-success-500'
            : '[&>p>b]:bg-primary-50 [&>p>b]:text-primary-700',
        )}
      >
        {item}
      </div>
    </li>
  )
}
